import React from "react";
import {
    REGISTER_URL, FACILITATOR_PHOTO, FACILITATOR_NAME, CREDENTIALS, FACILITATOR_QUOTE,
} from "@/lib/site";
import { Award, BookOpen, GraduationCap, PenLine, Briefcase, Quote } from "lucide-react";

const ICONS = [GraduationCap, BookOpen, PenLine, Award, Briefcase];

function Portrait() {
    if (FACILITATOR_PHOTO) {
        return (
            <div className="portrait-frame" data-testid="facilitator-photo">
                <div className="relative rounded-[18px] overflow-hidden aspect-[4/5] bg-[color:var(--paper-tint)]">
                    <img
                        src={FACILITATOR_PHOTO}
                    alt={`${FACILITATOR_NAME}, professional portrait`}
                        className="w-full h-full object-cover object-top relative z-0"
                        loading="lazy"
                    />
                    {/* subtle warm overlay to harmonise with page tone */}
                    <div
                        className="absolute inset-0 pointer-events-none z-[2]"
                        style={{
                            background:
                                "linear-gradient(180deg, rgba(15,30,66,0) 55%, rgba(15,30,66,0.28) 100%)",
                        }}
                    />
                    {/* corner name plate */}
                    <div className="absolute bottom-4 left-4 right-4 z-[3] flex items-end justify-between gap-3">
                        <div className="rounded-xl bg-white/85 backdrop-blur-sm border border-white/60 px-3.5 py-2 shadow-sm">
                            <div className="font-mono-label text-[color:var(--maroon)]">
                                Facilitator
                            </div>
                            <div className="mt-0.5 font-serif text-base text-[color:var(--navy)] leading-tight">
                                Prof. Dr. Aini Aman
                            </div>
                        </div>
                        <div className="hidden sm:block h-px flex-1 bg-[color:var(--gold-soft)]/70" />
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="portrait-frame" data-testid="facilitator-photo-placeholder">
            <div
                className="relative rounded-[18px] overflow-hidden h-[580px] flex flex-col items-center justify-center text-center px-8"
                style={{
                    background:
                        "linear-gradient(160deg, #0f1e42 0%, #1c2e5e 45%, #7a1f2f 100%)",
                }}
            >
                <div className="absolute -top-16 -right-16 h-72 w-72 rounded-full border border-[color:var(--gold)]/30" />
                <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full border border-[color:var(--gold)]/20" />
                <div className="absolute inset-6 rounded-[14px] border border-[color:var(--gold)]/25" />
                <div className="relative z-10 flex flex-col items-center gap-6">
                    <div className="h-28 w-28 rounded-full bg-[color:var(--gold)]/15 border border-[color:var(--gold)]/40 flex items-center justify-center">
                        <span className="font-serif text-5xl text-[color:var(--gold-soft)]">AA</span>
                    </div>
                    <div className="font-mono-label text-[color:var(--gold-soft)]">
                        Facilitator Portrait
                    </div>
                    <div className="font-serif text-2xl text-white leading-tight">
                        Prof. Dr. Aini Aman
                        <span className="block italic text-white/70 text-base mt-1">
                            professional photo to be inserted here
                        </span>
                    </div>
                    <div className="mt-2 h-px w-16 bg-[color:var(--gold)]/60" />
                    <div className="text-xs text-white/60 max-w-[240px] leading-relaxed">
                        The latest professional portrait of Prof. Dr. Aini Aman will appear in this frame.
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Facilitator() {
    return (
        <section
            id="facilitator"
            data-testid="facilitator-section"
            className="py-20 md:py-28 bg-[color:var(--beige-soft)] border-y border-[color:var(--line)]"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-4">
                        <div className="max-w-[340px] mx-auto lg:mx-0 lg:sticky lg:top-24">
                            <Portrait />
                        </div>
                    </div>

                    <div className="lg:col-span-8">
                        <div className="font-mono-label text-[color:var(--maroon)]">
                            Meet Your Facilitator
                        </div>
                        <h2
                            className="mt-3 font-serif text-4xl sm:text-5xl text-[color:var(--navy)] tracking-tight leading-tight"
                            style={{ fontWeight: 500 }}
                        >
                            Learn with Prof. Dr. Aini Aman.
                        </h2>
                        <div className="hr-gold mt-5" />

                        <p className="mt-6 text-slate-700 leading-relaxed text-base sm:text-lg dropcap">
                            Prof. Dr. Aini Aman is an experienced professor, researcher, consultant and
                            trainer with extensive experience in qualitative research, interpretive
                            research, accounting information systems, digital transformation and
                            academic publication. She has supervised postgraduate researchers, led
                            funded research and consultancy projects, and trained students, lecturers
                            and professionals in developing practical, rigorous and publishable
                            research work.
                        </p>

                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {CREDENTIALS.map((title, i) => {
                                const Icon = ICONS[i] || Award;
                                return (
                                    <div
                                        key={title}
                                        data-testid={`credibility-card-${i + 1}`}
                                        className={`rounded-2xl bg-white border border-[color:var(--line)] p-4 flex items-center gap-3 ${
                                            i === CREDENTIALS.length - 1 && CREDENTIALS.length % 2 === 1
                                                ? "sm:col-span-2"
                                                : ""
                                        }`}
                                    >
                                        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[color:var(--paper-tint)] text-[color:var(--maroon)] border border-[color:var(--line)]">
                                            <Icon className="h-5 w-5" />
                                        </span>
                                        <div className="font-serif text-lg text-[color:var(--navy)] leading-tight">
                                            {title}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Quote box */}
                        <div className="mt-8 rounded-3xl bg-[color:var(--navy)] text-white p-7 sm:p-8 relative overflow-hidden">
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    backgroundImage:
                                        "radial-gradient(circle at 90% 10%, rgba(181,138,43,0.18), transparent 45%)",
                                }}
                            />
                            <Quote className="relative h-8 w-8 text-[color:var(--gold-soft)]" />
                            <p
                                data-testid="facilitator-quote"
                                className="relative mt-4 font-serif text-xl sm:text-2xl leading-snug italic"
                            >
                                “{FACILITATOR_QUOTE.text}”
                            </p>
                            <div className="relative mt-4 font-mono-label text-[color:var(--gold-soft)]">
                                {FACILITATOR_QUOTE.attribution}
                            </div>
                        </div>

                        <a
                            href={REGISTER_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="facilitator-register-btn"
                            className="btn-maroon mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm"
                        >
                            Register with Prof. Dr. Aini Aman
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
